import { Observable } from 'rxjs/internal/Observable';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GenerateBreadcrumbUseCaseResponse } from './generate-breadcrumb.use-case-response';
import { UseCase } from '../../shared/use-case';
import { LogService } from '../../shared/log.service';
import { DynamicSubMenuItem, NavigationMenuItem } from '../../navigation/navigation-menu';
import { map, take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { Breadcrumb } from '../breadcrumb.model';
import { BusinessLogicError } from '../../shared/business-logic.error';
import { GenerateBreadcrumbUseCaseRequest } from './generate-breadcrumb.use-case-request';
import { ValidationError } from '../../shared/validation.error';
import { TranslationService } from '../../translation/translation.service';
import { BreadcrumbConfig } from '../breadcrumb.config';

@Injectable({
    providedIn: 'root'
})
export class GenerateBreadcrumbUseCase extends UseCase<
    GenerateBreadcrumbUseCaseRequest,
    Observable<GenerateBreadcrumbUseCaseResponse>
> {
    constructor(
        _logService: LogService,
        _httpClient: HttpClient,
        _breadcrumbConfig: BreadcrumbConfig,
        private _translationService: TranslationService
    ) {
        super(_logService, _httpClient, _breadcrumbConfig.enableUseCaseStatusLogs);
    }

    protected override validate(request: GenerateBreadcrumbUseCaseRequest) {
        if (!request.navigationMenuConfigPath) {
            throw new ValidationError('Navigation Menu Config Path cannot be empty!');
        }
    }

    protected executeBusinessLogic(request: GenerateBreadcrumbUseCaseRequest): Observable<GenerateBreadcrumbUseCaseResponse> {
        const navigationMenuObservable = this._http.get<NavigationMenuItem[]>(request.navigationMenuConfigPath).pipe(take(1));
        const dynamicSubMenuObservable = this._http.get<DynamicSubMenuItem[]>("./assets/configurations/dynamic-sub-menu.json").pipe(take(1)); //TODO call backend instead
        return forkJoin([navigationMenuObservable, dynamicSubMenuObservable]).pipe(map(
            ([navigationMenu, dynamicSubMenu]) => {
                const response: GenerateBreadcrumbUseCaseResponse = new GenerateBreadcrumbUseCaseResponse();
                response.breadcrumbItems.push(this.generateHomeBreadcrumbItem(navigationMenu));
                response.breadcrumbItems = response.breadcrumbItems.concat(this.generateBreadcrumbItems(navigationMenu, dynamicSubMenu));
                return response;
            }
        ));
    }

    private generateHomeBreadcrumbItem(navigationMenus: NavigationMenuItem[]): Breadcrumb {
        let homeBreadcrumbItem: Breadcrumb | undefined = undefined;
        navigationMenus.forEach ((navigationMenuItem: NavigationMenuItem) => {
            if (navigationMenuItem.isDefault) {
                if (homeBreadcrumbItem) {
                    throw new BusinessLogicError("Navigation menu config has to contain exactly one home item!");
                }
                homeBreadcrumbItem = { title: this._translationService.translate(navigationMenuItem.translationKey!), routerLink: "/" };
            }
        });
        if (!homeBreadcrumbItem) {
            throw new BusinessLogicError("Navigation menu config has to contain a home item!");
        }
        return homeBreadcrumbItem;
    }

    private generateBreadcrumbItems(navigationMenu: NavigationMenuItem[], dynamicSubMenu: DynamicSubMenuItem[]): Breadcrumb[] {
        const breadcrumbItems: Breadcrumb[] = [];
        let previousBreadcrumbItemHasDynamicSubmenu = false;
        const urlParts: string[] = window.location.pathname.split('/');
        urlParts.shift();
        urlParts.forEach((urlPart: string) => {
            navigationMenu.forEach ((navigationMenuItem: NavigationMenuItem) => {
                if (!navigationMenuItem.isDefault && urlPart === navigationMenuItem.id) {
                    let navigationItemRouterLink = "/" + navigationMenuItem.id;
                    let tempNavigationItem = { ...navigationMenuItem };
                    while (tempNavigationItem.parentMenu) {
                        tempNavigationItem = tempNavigationItem.parentMenu;
                        navigationItemRouterLink = tempNavigationItem.id + "/" + navigationItemRouterLink;
                    }
                    previousBreadcrumbItemHasDynamicSubmenu = navigationMenuItem.hasDynamicSubMenu!;
                    breadcrumbItems.push({ title: this._translationService.translate(navigationMenuItem.translationKey!), routerLink: navigationItemRouterLink});
                }
            })
            if (previousBreadcrumbItemHasDynamicSubmenu) {
                dynamicSubMenu.forEach((dynamicSubMenuItem: DynamicSubMenuItem) => {
                    if(urlPart === dynamicSubMenuItem.id) {
                        breadcrumbItems.push({ title: dynamicSubMenuItem.title!, routerLink: ''});
                    }
                })
                return;
            }
        });
        return breadcrumbItems;
    }
}
